import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  LoginRequest,
  TokenResponse,
  VerifyPasswordRequest,
} from "../../domain/types/auth";
import { findWorkspaceSelection } from "../../domain/types/auth";
import { STORAGE_KEYS } from "../../shared/constants/storage";
import type { AppError } from "../../shared/utils/error-handler";
import { StorageService } from "../../infrastructure/storage/storage-service";
import { userUseCase } from "../../di/container";
import type { LandingUserInfoResponse } from "../../domain/types/shared";

export interface AuthState {
  // State
  isAuthenticated: boolean;
  user: LandingUserInfoResponse | null;
  accessToken: string | null;
  isLoading: boolean;
  error: AppError | null;

  // Actions
  login: (request: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  verifyPassword: (request: VerifyPasswordRequest) => Promise<boolean>;
  refreshToken: () => Promise<void>;
  socialLogin: (
    providerUserToken: string,
    providerUserId: string,
    companyId?: number,
  ) => Promise<void>;
  /** 로그인 API 밖에서 받은 토큰(초대 수락, 워크스페이스 선택)으로 로그인을 마무리한다 */
  completeLogin: (tokenResponse: TokenResponse) => Promise<void>;
  /** 워크스페이스 선택 → 토큰 → 로그인 마무리 */
  selectWorkspace: (selectionToken: string, userId: number) => Promise<TokenResponse>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;

  // Computed
  isAdmin: () => boolean;
  isCompanyAdmin: () => boolean;
  isCompanyManager: () => boolean;
  /** 회사 관리 영역(MES) 접근 가능 — COMPANY_ADMIN 또는 COMPANY_MANAGER. */
  canAccessCompany: () => boolean;
}

const storageService = new StorageService();

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      user: null,
      accessToken: null,
      isLoading: false,
      error: null,

      // Actions
      login: async (
        request: LoginRequest,
      ) => {
        const { authUseCase } = await import("../../di/container");

        set({ isLoading: true, error: null });

        try {
          const loginResponse = await authUseCase.login(
            request,
true
          );
          const accessToken = loginResponse.accessToken;
          const user = await userUseCase.getUserInfo();

          set({
            isAuthenticated: true,
            user: user,
            accessToken: accessToken,
            isLoading: false,
          });
        } catch (error) {
          // 워크스페이스 선택은 실패가 아니다 — 화면이 선택 다이얼로그를 띄운다
          set({
            error: findWorkspaceSelection(error) ? null : (error as AppError),
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        const { authUseCase } = await import("../../di/container");

        set({ isLoading: true });

        try {
          await authUseCase.logout();

          // StorageService에서 토큰 제거
          storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          storageService.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

          set({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          // 로그아웃은 로컬에서도 처리
          storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          storageService.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

          set({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            isLoading: false,
            error: error as AppError,
          });
        }
      },

      verifyPassword: async (request) => {
        const { authUseCase } = await import("../../di/container");

        set({ isLoading: false, error: null });

        try {
          const response = await authUseCase.verifyPassword(request);

          set({
            isLoading: false,
            error: null,
          });

          return response;
        } catch (error) {
          set({
            isLoading: false,
            error: error as AppError,
          });

          throw error;
        }
      },
      refreshToken: async () => {
        // Swagger API doesn't support refresh tokens
        // Force logout and require re-login
        console.warn("Token refresh not supported, forcing logout");
        get().logout();
        throw new Error("Token refresh not supported. Please login again.");
      },

      socialLogin: async (
        providerUserToken: string,
        providerUserId: string,
        companyId?: number,
      ) => {
        const { authUseCase } = await import("../../di/container");

        set({ isLoading: true, error: null });

        try {
          const authEntity = await authUseCase.socialLogin({
            providerUserToken,
            providerUserId,
            companyId,
          });
          const userId = authEntity.userId;
          const accessToken = authEntity.accessToken;
          const user = await userUseCase.getUserInfo();

          // 토큰은 이미 auth-usecase에서 저장됨

          set({
            isAuthenticated: true,
            user: user,
            accessToken: accessToken,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error as AppError,
            isLoading: false,
          });
          throw error;
        }
      },

      completeLogin: async (tokenResponse: TokenResponse) => {
        const { authUseCase } = await import("../../di/container");

        set({ isLoading: true, error: null });

        try {
          authUseCase.saveTokens(tokenResponse);
          const user = await userUseCase.getUserInfo();

          set({
            isAuthenticated: true,
            user: user,
            accessToken: tokenResponse.accessToken,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error as AppError,
            isLoading: false,
          });
          throw error;
        }
      },

      selectWorkspace: async (selectionToken: string, userId: number) => {
        const { authUseCase } = await import("../../di/container");
        const tokenResponse = await authUseCase.selectWorkspace(selectionToken, userId);
        await get().completeLogin(tokenResponse);
        return tokenResponse;
      },

      clearError: () => set({ error: null }),

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      // Computed
      isAdmin: () => {
        const { user } = get();
        return user?.role === "ADMIN" || false;
      },

      isCompanyAdmin: () => {
        const { user } = get();
        return user?.role === "COMPANY_ADMIN" || false;
      },

      isCompanyManager: () => {
        const { user } = get();
        return user?.role === "COMPANY_MANAGER" || false;
      },

      // 매니저는 COMPANY_ADMIN 과 유사 접근권한(직원 권한변경만 제외) — 회사 MES 진입 허용.
      canAccessCompany: () => {
        const { user } = get();
        return user?.role === "COMPANY_ADMIN" || user?.role === "COMPANY_MANAGER" || false;
      },
    }),
    {
      name: STORAGE_KEYS.USER_INFO,
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        accessToken: state.accessToken,
      }),
    },
  ),
);
